import React, { useState, useEffect, useRef } from "react";
import { Sparkles, Check, Loader2, Brain, Building2, Camera, Palette, Search, Share2 } from "lucide-react";
import { authApi } from "./authApi";

// Moteur "1 clic" partagé : pack + photos + styles → génère titre, description,
// points forts, SEO, commune, activités et home staging EN PARALLÈLE, avec une
// barre de progression unique. Utilisé dans l'éditeur d'annonce ET la Visite IA.
export function OneClickGenerate({ slug, images, defaultIncludeText = true, refineNote = false, compact = false, autoPack = false, hideUpsell = false, onDone }) {
  const [cfg, setCfg] = useState(null);
  const [styles, setStyles] = useState([]);
  const [pack, setPack] = useState(compact ? "premium" : "classique");
  const [selImgs, setSelImgs] = useState(() => new Set(images || []));
  const [selStyles, setSelStyles] = useState(() => new Set(["contemporain"]));
  const [incText, setIncText] = useState(defaultIncludeText);
  const [busy, setBusy] = useState(false);
  const [prog, setProg] = useState(null);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const mountedRef = useRef(true);
  const pollRef = useRef(null);
  useEffect(() => () => { mountedRef.current = false; if (pollRef.current) clearTimeout(pollRef.current); }, []);

  useEffect(() => {
    authApi.creditsConfig().then(setCfg).catch(() => {});
    authApi.stagingStyles().then((d) => setStyles(d.styles || [])).catch(() => {});
    // Si l'annonce est déjà générée, refléter l'état "terminé" pour éviter une relance accidentelle.
    authApi.generationStatus(slug).then((s) => { if (mountedRef.current && s && s.status === "done") setDone(true); }).catch(() => {});
  }, [slug]);
  useEffect(() => { setSelImgs(new Set(images || [])); }, [images]);

  const tiers = cfg?.tiers || {};
  const tier = tiers[pack] || {};
  const cap = tier.max_staging || 6;
  const combos = Math.min(cap, selImgs.size * selStyles.size);
  const toggle = (set, setter, v) => { const n = new Set(set); n.has(v) ? n.delete(v) : n.add(v); setter(n); };

  // Auto-sélection du bon pack selon le nombre de rendus (photos × styles) : le plus petit pack suffisant.
  const needed = selImgs.size * selStyles.size;
  useEffect(() => {
    if (!autoPack || !cfg) return;
    const keys = ["classique", "pro", "premium"].filter((k) => tiers[k]);
    if (!keys.length) return;
    const fit = keys.find((k) => (tiers[k].max_staging || 0) >= needed) || keys[keys.length - 1];
    setPack((p) => (fit && fit !== p ? fit : p));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPack, cfg, needed]);

  const run = async () => {
    if (busy || done) return; // idempotence : pas de relance si en cours ou déjà terminé
    setBusy(true); setErr(""); setDone(false); setProg({ total: 1, done: 0, label: "Initialisation…" });
    try {
      await authApi.generateAll(slug, { pack, images: [...selImgs], styles: [...selStyles], include_text: incText, room: "Salon" });
      const poll = async () => {
        if (!mountedRef.current) return;
        try {
          const s = await authApi.generationStatus(slug);
          if (!mountedRef.current) return;
          if (s.progress && s.progress.total) setProg(s.progress);
          if (s.status === "done") { setBusy(false); setDone(true); onDone && onDone(); return; }
          if (s.status === "error") { setBusy(false); setErr(s.error || "Erreur pendant la génération"); return; }
        } catch (_) {}
        if (mountedRef.current) pollRef.current = setTimeout(poll, 2000);
      };
      pollRef.current = setTimeout(poll, 1500);
    } catch (e) {
      setBusy(false);
      const msg = e.detail?.message || e.message || "Échec du lancement";
      setErr(e.status === 402 || /crédit|credit|solde|insuffisant/i.test(msg)
        ? "Crédits insuffisants pour lancer cette génération. Rechargez vos crédits pour continuer."
        : msg);
    }
  };
  const pct = prog && prog.total ? Math.round((prog.done / prog.total) * 100) : 0;
  const PACK_KEYS = ["classique", "pro", "premium"];

  return (
    <div className="border border-[#C19A6B]/30 bg-[#C19A6B]/5 p-4 space-y-4" data-testid="oneclick-panel">
      <div className="flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-[#C19A6B]" />
        <p className="text-sm font-medium text-white">{compact ? "Home staging" : "Étape 2 · Home staging & finalisation"}</p>
      </div>
      {!compact && <p className="text-xs text-zinc-400 -mt-2">Un seul clic : titre, description, points forts, SEO, commune, activités et home staging — tout en parallèle.</p>}

      {!compact && (
      <div>
        <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">Pack</p>
        <div className="grid sm:grid-cols-3 gap-2">
          {PACK_KEYS.filter((k) => tiers[k]).map((k) => (
            <button key={k} type="button" onClick={() => setPack(k)} data-testid={`pack-${k}`}
              className={`text-left border p-3 transition-colors ${pack === k ? "border-[#C19A6B] bg-[#C19A6B]/10" : "border-white/15 hover:border-white/30"}`}>
              <span className={`text-sm font-semibold ${pack === k ? "text-[#C19A6B]" : "text-white"}`}>{tiers[k].label}</span>
              <span className="block text-[11px] text-zinc-400 mt-0.5">{tiers[k].cost} crédit{tiers[k].cost > 1 ? "s" : ""} · jusqu'à {tiers[k].max_staging} rendus</span>
            </button>
          ))}
        </div>
      </div>
      )}

      <div>
        <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">Photos à transformer ({selImgs.size})</p>
        <div className="flex flex-wrap gap-2">
          {(images || []).map((im, i) => (
            <button key={i} type="button" onClick={() => toggle(selImgs, setSelImgs, im)} data-testid={`ocg-img-${i}`}
              className={`relative border-2 ${selImgs.has(im) ? "border-[#C19A6B]" : "border-transparent opacity-60"}`}>
              <img src={im} alt="" className="w-16 h-12 object-cover" />
              {selImgs.has(im) && <span className="absolute top-0 right-0 bg-[#C19A6B] text-white"><Check className="w-3 h-3" /></span>}
            </button>
          ))}
          {(!images || images.length === 0) && <p className="text-[11px] text-amber-400">Ajoutez d'abord des photos.</p>}
        </div>
      </div>

      <div>
        <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">Styles de home staging ({selStyles.size})</p>
        <div className="flex flex-wrap gap-2">
          {styles.map((s) => (
            <button key={s.key} type="button" onClick={() => toggle(selStyles, setSelStyles, s.key)} data-testid={`ocg-style-${s.key}`}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${selStyles.has(s.key) ? "border-[#C19A6B] bg-[#C19A6B]/15 text-[#C19A6B]" : "border-white/15 text-zinc-300 hover:border-white/30"}`}>
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {!compact && (
      <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer" data-testid="ocg-inctext">
        <input type="checkbox" checked={incText} onChange={(e) => setIncText(e.target.checked)} className="accent-[#C19A6B]" />
        Générer aussi les textes, SEO, commune & activités {refineNote ? "(vos textes de visite seront affinés)" : ""}
      </label>
      )}

      <div className="flex items-center justify-between text-xs text-zinc-300 border-t border-white/10 pt-3">
        <span><b className="text-white">{combos}</b> rendu{combos > 1 ? "s" : ""} de home staging{!compact ? ` (plafond ${cap})` : ""}</span>
        {(!compact || autoPack) && <span className="text-[#C19A6B]">Coût publication : {tier.cost || 1} crédit{(tier.cost || 1) > 1 ? "s" : ""}</span>}
      </div>
      {autoPack && tier.label && (
        <div className="flex items-center gap-2 text-[11px] text-zinc-400 -mt-2" data-testid="ocg-autopack">
          <span className="text-[#C19A6B] font-semibold">Formule automatique : {tier.label}</span>
          <span>· {needed} rendu{needed > 1 ? "s" : ""} sélectionné{needed > 1 ? "s" : ""} · {tier.cost || 1} crédit{(tier.cost || 1) > 1 ? "s" : ""} à la publication</span>
        </div>
      )}
      {!hideUpsell && selImgs.size * selStyles.size > cap && (() => {
        const need = selImgs.size * selStyles.size;
        const canPro = tiers.pro && tiers.pro.max_staging >= need && pack !== "pro" && pack !== "premium";
        const canPrem = tiers.premium && pack !== "premium";
        return (
          <div className="border border-[#C19A6B]/40 bg-[#C19A6B]/10 p-4 space-y-3 rounded-lg" data-testid="ocg-upsell">
            <p className="text-sm text-white font-medium">Votre projet nécessite davantage de contenu Home Staging</p>
            <p className="text-xs text-zinc-300">Vous avez sélectionné <b className="text-[#C19A6B]">{need} rendus</b>, au-delà du plafond de votre formule ({cap}). Passez à une offre supérieure pour tout générer et profiter d'une présentation plus complète.</p>
            <div className="flex flex-wrap gap-2">
              {canPro && <button type="button" onClick={() => setPack("pro")} data-testid="upsell-pro" className="inline-flex items-center gap-1.5 border border-[#C19A6B] text-[#C19A6B] hover:bg-[#C19A6B]/15 text-xs font-semibold uppercase tracking-wider px-4 py-2 rounded transition-colors">Continuer en Pro · {tiers.pro.cost} cr · {tiers.pro.max_staging} rendus</button>}
              {canPrem && <button type="button" onClick={() => setPack("premium")} data-testid="upsell-premium" className="inline-flex items-center gap-1.5 bg-[#C19A6B] hover:bg-[#d1af84] text-white text-xs font-semibold uppercase tracking-wider px-4 py-2 rounded transition-colors">Passer en Premium · {tiers.premium.cost} cr · {tiers.premium.max_staging} rendus</button>}
            </div>
            <p className="text-[10px] text-zinc-400">Le coût est recalculé automatiquement. Tout votre travail est conservé — la génération reprend sans recommencer.</p>
          </div>
        );
      })()}

      {busy && (() => {
        const lbl = (prog?.label || "").toLowerCase();
        const step =
          /text|texte|titre|descr|présent|present|rédac|redac/.test(lbl) ? { Ic: Brain, t: "Génération des textes", e: "Rédaction du titre, de la description et des points forts…" } :
          /commune|ville|quartier|local|secteur/.test(lbl) ? { Ic: Building2, t: "Analyse de la commune", e: "Atouts du secteur, commerces, écoles et transports…" } :
          /staging|home|photo|image|pièce|piece|salon/.test(lbl) ? { Ic: Camera, t: "Home Staging IA", e: "Transformation de vos photos en intérieurs sublimés…" } :
          /ambiance|style|rendu|décor|decor/.test(lbl) ? { Ic: Palette, t: "Génération des ambiances", e: "Création des styles et atmosphères choisis…" } :
          /seo|référ|refer|mots/.test(lbl) ? { Ic: Search, t: "Optimisation SEO", e: "Mots-clés et métadonnées pour être bien référencé…" } :
          /réseau|reseau|social|partage|publi/.test(lbl) ? { Ic: Share2, t: "Publications réseaux", e: "Préparation des textes de partage sur les réseaux sociaux…" } :
          { Ic: Sparkles, t: "Initialisation", e: "Préparation de votre annonce premium…" };
        const Ic = step.Ic;
        return (
          <div data-testid="ocg-progress" className="border border-[#C19A6B]/30 bg-[#C19A6B]/[0.06] rounded-xl p-5 text-center">
            <div className="flex justify-center mb-3">
              <span className="relative w-16 h-16 rounded-full bg-[#C19A6B]/15 flex items-center justify-center">
                <span className="absolute inset-0 rounded-full border-2 border-[#C19A6B]/40 border-t-[#C19A6B] animate-spin" />
                <Ic className="w-7 h-7 text-[#C19A6B] animate-pulse" />
              </span>
            </div>
            <p className="text-white font-semibold text-sm" data-testid="ocg-step-title">{step.t}</p>
            <p className="text-xs text-zinc-400 mt-0.5 mb-3">{step.e}</p>
            <div className="h-2.5 bg-white/10 rounded-full overflow-hidden"><div className="h-full bg-gradient-to-r from-[#d1af84] to-[#C19A6B] transition-all duration-500" style={{ width: `${pct}%` }} /></div>
            <div className="flex items-center justify-between text-[11px] text-zinc-400 mt-1.5">
              <span className="truncate">{prog?.label || "Génération en cours…"}</span>
              <span className="font-bold text-[#C19A6B]">{pct}%</span>
            </div>
          </div>
        );
      })()}
      {done && <p className="text-sm text-green-400 flex items-center gap-2" data-testid="ocg-done"><Check className="w-4 h-4" /> Génération terminée ! Votre annonce enrichie est prête à prévisualiser.</p>}
      {err && <div className="text-sm text-red-400" data-testid="ocg-error">{err}{/crédit|insuffisant/i.test(err) && <a href="/mon-compte" className="block mt-2 text-[#C19A6B] underline font-semibold" data-testid="ocg-buy-credits">Acheter des crédits →</a>}</div>}

      <button type="button" onClick={run} disabled={busy || done || selImgs.size === 0 || selStyles.size === 0} data-testid="ocg-generate"
        className="w-full inline-flex items-center justify-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] disabled:opacity-50 text-white font-semibold uppercase tracking-wider text-sm px-6 py-3 transition-colors">
        {done ? <Check className="w-4 h-4" /> : busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {done ? "Génération terminée" : busy ? "Génération en cours…" : "Générer avec l'IA"}
      </button>
    </div>
  );
}

export default OneClickGenerate;
